import { useEffect, useMemo, useState } from 'react';
import { Route, Switch, useRouteMatch, useLocation } from 'react-router-dom';
import { ReportSubscriptionSubscription } from '../generated/hooks';
import { buildQuestionItems } from './newReport/buildQuestionItems';
import { ReportDetailHeader } from './newReport/components/ReportDetailHeader';
import { useReportFiltersUrl } from './newReport/hooks/useReportFiltersUrl';
import { useHeaderCompactOnScroll } from './newReport/hooks/useHeaderCompactOnScroll';
import { useReportLanguage } from './newReport/hooks/useReportLanguage';
import { reportScope, useNewReport, useNewReportData } from './newReport/hooks/useNewReport';
import { ReportDefinitionProvider } from './newReport/definition/ReportDefinitionContext';
import { buildTeamNodes } from './newReport/buildTeamNodes';
import { useTranslation } from 'react-i18next';
import { ReportMenu } from './newReport/ReportMenu';
import { theme } from '@arnold/common';
import Div100vh from 'react-div-100vh';
import { REPORT_ROUTES, useReportMenuItems } from './newReport/reportMenuItems';
import { Loading } from '../components';
import { Error } from './components/Error';
import { SOMEONE_ELSE_REPORT } from '../graphql/errors';
import { getAnonymityMode } from '../lib/reportHelper';
import { Body, Content, Main } from './newReportPageStyles';
import ReportOverview from './newReport/components/ReportOverview';
import { TableTestPage } from './newReport/pages/TableTestPage';
import { EditorDock } from './newReport/definition/editor/EditorDock';
import { OverviewEditorProvider } from './newReport/definition/editor/OverviewEditorContext';
import { QuestionDetailPage } from './newReport/pages/QuestionDetailPage';
import { MetricDetailPage } from './newReport/pages/MetricDetailPage';
import { ReportFooter } from './newReport/components/ReportFooter';
import { DeepAnalysisPage } from './newReport/pages/DeepAnalysisPage';

// Div100vh resolves `rvh` into a pixel height through an inline style, so it has to own the layout styles too —
// wrapping it in `styled()` leaves the shell without a height until after mount.
const layoutStyle = {
  height: '100rvh',
  display: 'flex',
  alignItems: 'stretch',
  backgroundColor: theme.colors.backgroundBasic.default,
} as const;

interface ITestReportProps {
  accessToken: string;
  selectedLanguage: string;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'] | null,
  ) => void;
  setAllowedLanguages: (languages: string[]) => void;
  allowedLanguages?: string[];
  setNavbarContent?: (content: React.ReactNode) => void;
  setLocalization: (lang: string) => any;
  initLanguage: string;
}

type ReportData = ReturnType<typeof useNewReportData>;

/**
 * The definition draft lives in a provider above the page content, so editing a title re-renders only the
 * components that read the draft (overview, editor dock), not the header, the menu or the filters.
 */
const NewReportPage = (props: ITestReportProps): JSX.Element => {
  const { url } = useRouteMatch();
  const { loading, error, data } = useNewReportData(props.accessToken, props.selectedLanguage, false);
  return (
    <ReportDefinitionProvider report={data?.getNewReport} scope={reportScope(props.accessToken, false)} basePath={url}>
      <NewReportContent {...props} loading={loading} error={error} data={data} />
    </ReportDefinitionProvider>
  );
};

const NewReportContent = ({ loading, error, data, ...props }: ITestReportProps & ReportData): JSX.Element => {
  // `path` keeps the `:token` placeholder for the <Route>s below, while `url` is the resolved address the menu
  // links have to point at.
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const questionRoute = useRouteMatch<{ questionIndex: string }>(`${path}/detail/:questionIndex`);
  const metricRoute = useRouteMatch<{ metricId: string }>(`${path}/metric/:metricId`);
  const overviewRoute = useRouteMatch({ path, exact: true });
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);

  const report = useNewReport(props.accessToken, props.selectedLanguage, false, data);
  const { contentRef, isCompact } = useHeaderCompactOnScroll(report.rawData);
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [pathname, contentRef]);

  const { setFilters } = report;
  const { t } = useTranslation('newReport');
  const { t: tReport } = useTranslation('Report');
  const menuItems = useReportMenuItems(url, t);
  const organizationLanguageCodes = useMemo(
    () => report.rawData?.metadata?.organization?.languages?.map((language) => language.code),
    [report.rawData],
  );
  const teams = useMemo(
    () => (report.rawData ? buildTeamNodes(report.rawData, tReport) : []),
    [report.rawData, tReport],
  );
  const questions = useMemo(() => (report.rawData ? buildQuestionItems(report.rawData) : []), [report.rawData]);
  const rootTeamNames = useMemo(
    () =>
      report.rawData?.reportRoots.map((rootId) => {
        const team = report.rawData?.teams.find((team) => String(team.id) === String(rootId));

        return team && team.teamName
          ? team.teamName
          : team?.teamName === ''
            ? tReport('withoutSupervisor')
            : String(rootId);
      }) ?? [],
    [report.rawData, tReport],
  ).sort((a, b) => a.localeCompare(b));
  const { selectedTeams, selectedAnswers, setSelectedTeams, setSelectedAnswers } = useReportFiltersUrl(
    teams,
    questions,
  );
  const filtersProps = useMemo(
    () => ({
      teams,
      questions,
      selectedTeams,
      onSelectedTeamsChange: setSelectedTeams,
      selectedAnswers,
      onSelectedAnswersChange: setSelectedAnswers,
    }),
    [teams, questions, selectedTeams, setSelectedTeams, selectedAnswers, setSelectedAnswers],
  );

  const { selectedLanguage, changeLanguage, user } = useReportLanguage({
    initLanguage: props.initLanguage,
    activeLanguage: props.selectedLanguage,
    organizationLanguageCodes,
    reportLanguageCode: report.rawData?.metadata?.language?.code,
    organizationPrimaryLanguageCode: (report.rawData?.metadata?.organization as any)?.primaryLanguageCode,
    setAllowedLanguages: props.setAllowedLanguages,
    setLocalization: props.setLocalization,
  });

  useEffect(() => {
    setFilters({ teamIds: selectedTeams, answerSelections: selectedAnswers });
  }, [selectedTeams, selectedAnswers, setFilters]);

  useEffect(() => {
    if (!report.rawData) {
      return;
    }
    const title =
      report.rawData?.topicGroup.translations?.find(
        (translation) => translation.language.code === props.selectedLanguage,
      )?.value || report.rawData?.topicGroup.translations?.[0]?.value;
    const description =
      report.rawData?.topicGroup.translations?.find(
        (translation) => translation.language.code === props.selectedLanguage,
      )?.description ||
      report.rawData?.topicGroup.translations?.[0]?.description ||
      undefined;
    setTitle(title);
    setDescription(description);
  }, [report.rawData, props.selectedLanguage]);

  if (!loading && error) {
    return <Error>{tReport(error.message === SOMEONE_ELSE_REPORT ? 'someoneElseReport' : 'errorLoading')}</Error>;
  }

  if (!report.rawData) {
    return <Loading />;
  }

  const anonymityMode = getAnonymityMode(report.rawData.surveys, report.rawData.anonymitySettings);
  const detailQuestion = report.rawData.questions.find(
    (question) => question.index === questionRoute?.params.questionIndex,
  );

  return (
    <OverviewEditorProvider report={report}>
      <Div100vh style={layoutStyle}>
        {/* The filters live in the query string, so they have to survive navigation between sections. */}
        <ReportMenu items={menuItems} preserveQueryParams={true} />
        <Main>
          <Content ref={contentRef}>
            <ReportDetailHeader
              title={title}
              description={description}
              isCompact={isCompact}
              scrollerRef={contentRef}
              surveyStart={report.rawData.metadata.surveyStart}
              surveyEnd={report.rawData.metadata.surveyEnd}
              anonymityMode={anonymityMode}
              organizationName={report.rawData.metadata.organization.systemName}
              rootTeamNames={rootTeamNames}
              selectedLanguage={selectedLanguage}
              allowedLanguages={props.allowedLanguages}
              onChangeLanguage={changeLanguage}
              user={user}
              filtersProps={filtersProps}
              overviewUrl={url}
              detailQuestion={detailQuestion}
              detailMetricId={metricRoute?.params.metricId}
              currentGroup={report.currentGroup}
            />
            <Body>
              <Switch>
                <Route exact={true} path={path}>
                  <ReportOverview report={report} />
                </Route>
                <Route path={`${path}${REPORT_ROUTES.tableTest}`}>
                  <TableTestPage report={report} />
                </Route>
                <Route path={`${path}${REPORT_ROUTES.explore}`}>
                  <DeepAnalysisPage report={report} />
                </Route>
                <Route path={`${path}${REPORT_ROUTES.trends}`}>
                  <h1>{t('menu.trends')}</h1>
                </Route>
                <Route path={`${path}${REPORT_ROUTES.leaderboard}`}>
                  <h1>{t('menu.leaderboard')}</h1>
                </Route>
                <Route path={`${path}${REPORT_ROUTES.heatmap}`}>
                  <h1>{t('menu.heatmap')}</h1>
                </Route>
                <Route path={`${path}${REPORT_ROUTES.detail}`}>
                  <QuestionDetailPage key={questionRoute?.params.questionIndex} report={report} />
                </Route>
                <Route path={`${path}${REPORT_ROUTES.metric}`}>
                  <MetricDetailPage report={report} />
                </Route>
              </Switch>
            </Body>
            <ReportFooter />
          </Content>
        </Main>
        {overviewRoute && <EditorDock key={props.accessToken} report={report} />}
      </Div100vh>
    </OverviewEditorProvider>
  );
};

export default NewReportPage;
